import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Button } from "./button";
import { Input, type InputProps } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../../lib/utils";

type PhoneInputProps = Omit<ComponentProps<typeof RPNInput.default>, "onChange"> & {
  onChange?: (value: RPNInput.Value) => void;
};

type ComponentProps<T extends React.ElementType> = React.ComponentProps<T>;

const PhoneInput = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn("flex w-full", className)}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      {...props}
      onChange={(value) => onChange?.(value || ("" as RPNInput.Value))}
    />
  );
});

PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      className={cn("rounded-s-none rounded-e-xl", className)}
      {...props}
    />
  ),
);

InputComponent.displayName = "PhoneInputInput";

type CountrySelectOption = {
  label: string;
  value: RPNInput.Country;
};

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
};

function CountrySelect({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          className="h-11 gap-2 rounded-e-none rounded-s-xl border border-input border-r-0 px-3 hover:bg-accent"
          disabled={disabled}
        >
          <FlagComponent country={value} countryName={value} />
          <ChevronsUpDown className={cn("h-4 w-4 opacity-60", disabled && "hidden")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-80 overflow-y-auto p-1">
        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Velg land
        </div>
        <div className="space-y-1">
          {options
            .filter((option) => option.value)
            .map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <FlagComponent country={option.value} countryName={option.label} />
                <span className="flex-1">{option.label}</span>
                <span className="text-muted-foreground">
                  +{RPNInput.getCountryCallingCode(option.value)}
                </span>
                <CheckIcon
                  className={cn("h-4 w-4", option.value === value ? "opacity-100" : "opacity-0")}
                />
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FlagComponent({ country, countryName }: RPNInput.FlagProps) {
  const Flag = country ? flags[country] : undefined;

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm">
      {Flag ? <Flag title={countryName} /> : null}
    </span>
  );
}

export { PhoneInput };
